import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './MFAVerify.css';

interface MFAVerifyProps {
  email: string;
  onVerify: (code: string, isRecoveryCode: boolean, trustDevice: boolean, deviceName?: string) => Promise<void>;
  onCancel: () => void;
  deviceTrustEnabled?: boolean;
}

const MFAVerify: React.FC<MFAVerifyProps> = ({
  email,
  onVerify,
  onCancel,
  deviceTrustEnabled = true,
}) => {
  const { t } = useTranslation(['auth', 'common']);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Focus first input on mount
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all digits entered
    if (value && index === 5 && newCode.every(d => d)) {
      handleSubmit(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newCode = pastedData.split('');
      while (newCode.length < 6) newCode.push('');
      setCode(newCode);
      if (newCode.length === 6 && newCode.every(d => d)) {
        handleSubmit(newCode.join(''));
      }
    }
  };

  const handleSubmit = async (totpCode?: string) => {
    const codeToVerify = totpCode || code.join('');

    if (!useRecoveryCode && codeToVerify.length !== 6) {
      setError(t('auth:mfa.verify.invalidCode', 'Please enter a 6-digit code'));
      return;
    }

    if (useRecoveryCode && !recoveryCode.trim()) {
      setError(t('auth:mfa.verify.recoveryRequired', 'Please enter a recovery code'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await onVerify(
        useRecoveryCode ? recoveryCode.trim() : codeToVerify,
        useRecoveryCode,
        trustDevice,
        deviceName || undefined
      );
    } catch (err: any) {
      setError(err.message || t('auth:mfa.verify.failed', 'Verification failed'));
      // Clear code on error
      if (!useRecoveryCode) {
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mfa-verify-overlay">
      <div className="mfa-verify-card">
        <div className="mfa-verify-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
            <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <h2>{t('auth:mfa.verify.title', 'Two-Factor Authentication')}</h2>

        <p className="mfa-verify-email">{email}</p>

        {!useRecoveryCode ? (
          <>
            <p className="mfa-verify-description">
              {t('auth:mfa.verify.enterCode', 'Enter the 6-digit code from your authenticator app')}
            </p>

            <div className="mfa-code-inputs" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={el => inputRefs.current[index] = el}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="mfa-digit-input"
                  disabled={loading}
                  autoComplete="off"
                />
              ))}
            </div>

            <button
              className="mfa-use-recovery-link"
              onClick={() => setUseRecoveryCode(true)}
              disabled={loading}
            >
              {t('auth:mfa.verify.useRecoveryCode', 'Use a recovery code instead')}
            </button>
          </>
        ) : (
          <>
            <p className="mfa-verify-description">
              {t('auth:mfa.verify.enterRecovery', 'Enter one of your recovery codes')}
            </p>

            <div className="mfa-recovery-input-container">
              <input
                type="text"
                value={recoveryCode}
                onChange={(e) => {
                  setRecoveryCode(e.target.value.toUpperCase());
                  setError('');
                }}
                placeholder="XXXXXXXXXX"
                className="mfa-recovery-input"
                disabled={loading}
                autoFocus
              />
            </div>

            <button
              className="mfa-use-recovery-link"
              onClick={() => setUseRecoveryCode(false)}
              disabled={loading}
            >
              {t('auth:mfa.verify.useTOTP', 'Use authenticator app instead')}
            </button>
          </>
        )}

        {error && <div className="mfa-verify-error">{error}</div>}

        {deviceTrustEnabled && (
          <div className="mfa-trust-device">
            <label className="mfa-trust-checkbox">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                disabled={loading}
              />
              <span>{t('auth:mfa.verify.trustDevice', 'Trust this device for 30 days')}</span>
            </label>

            {trustDevice && (
              <input
                type="text"
                value={deviceName}
                onChange={(e) => setDeviceName(e.target.value)}
                placeholder={t('auth:mfa.verify.deviceName', 'Device name (optional)')}
                className="mfa-device-name-input"
                disabled={loading}
              />
            )}
          </div>
        )}

        <div className="mfa-verify-actions">
          <button
            className="mfa-verify-btn mfa-verify-btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            {t('common:cancel', 'Cancel')}
          </button>
          <button
            className="mfa-verify-btn mfa-verify-btn-primary"
            onClick={() => handleSubmit()}
            disabled={loading || (!useRecoveryCode && code.some(d => !d))}
          >
            {loading ? t('common:verifying', 'Verifying...') : t('common:verify', 'Verify')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MFAVerify;
